import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Notificacion {
  id: string;
  titulo: string;
  mensaje: string;
  tipo: string;
  leida: boolean;
  created_at: string;
  data: any;
}

export type NotificacionCategoria = 'todas' | 'urgentes' | 'citas' | 'mensajes' | 'admin' | 'sistema';

/* ─── Sound generation per priority ─── */
type SoundProfile = { freqs: [number, number]; duration: number; volume: number };

const SOUND_PROFILES: Record<string, SoundProfile> = {
  urgente:  { freqs: [880, 1320], duration: 0.8, volume: 1.0 },
  normal:   { freqs: [660, 880],  duration: 0.5, volume: 0.8 },
  info:     { freqs: [523, 659],  duration: 0.35, volume: 0.6 },
};

function getSoundProfile(tipo: string): SoundProfile {
  if (['solicitud_urgente', 'alerta_sos', 'admin_urgencia'].includes(tipo)) return SOUND_PROFILES.urgente;
  if (['nueva_solicitud', 'solicitud_aceptada', 'cita_confirmada', 'cita_cancelada', 'atencion_completada', 'nuevo_mensaje'].includes(tipo)) return SOUND_PROFILES.normal;
  return SOUND_PROFILES.info;
}

function getVibrationPattern(tipo: string): number[] {
  if (['solicitud_urgente', 'alerta_sos', 'admin_urgencia'].includes(tipo)) return [100, 50, 100, 50, 200];
  if (['nueva_solicitud', 'cita_confirmada', 'solicitud_aceptada'].includes(tipo)) return [150, 100, 150];
  return [200];
}

const soundCache: Record<string, string> = {};

function createWavForProfile(profile: SoundProfile): string {
  const key = JSON.stringify(profile);
  if (soundCache[key]) return soundCache[key];

  const sampleRate = 44100;
  const numSamples = Math.floor(sampleRate * profile.duration);
  const bitsPerSample = 16;
  const byteRate = sampleRate * (bitsPerSample / 8);
  const blockAlign = bitsPerSample / 8;
  const dataSize = numSamples * blockAlign;
  const headerSize = 44;
  const buffer = new ArrayBuffer(headerSize + dataSize);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  const [f1, f2] = profile.freqs;
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const env = Math.max(0, 1 - t / profile.duration) * (1 - Math.exp(-t * 80));
    const freq = t < profile.duration * 0.4 ? f1 : f2;
    const sample = Math.sin(2 * Math.PI * freq * t) * env;
    const harmonic = Math.sin(2 * Math.PI * freq * 2 * t) * env * 0.25;
    const combined = Math.max(-1, Math.min(1, (sample + harmonic) * profile.volume));
    view.setInt16(headerSize + i * 2, Math.floor(combined * 32767), true);
  }

  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  const url = 'data:audio/wav;base64,' + btoa(binary);
  soundCache[key] = url;
  return url;
}

function playNotificationSound(tipo: string) {
  try {
    const profile = getSoundProfile(tipo);
    const url = createWavForProfile(profile);
    const audio = new Audio(url);
    audio.volume = profile.volume;
    audio.play().catch(() => {});
  } catch {}
}

function vibrateDevice(tipo: string) {
  try {
    const pattern = getVibrationPattern(tipo);
    navigator?.vibrate?.(pattern);
  } catch {}
}

/* ─── Category helpers ─── */
export function getNotificacionCategoria(tipo: string): NotificacionCategoria {
  if (['solicitud_urgente', 'alerta_sos', 'admin_urgencia'].includes(tipo)) return 'urgentes';
  if (['cita_confirmada', 'cita_cancelada', 'atencion_completada', 'solicitud_aceptada', 'nueva_solicitud', 'solicitud_enviada', 'sin_profesionales', 'solicitud_liberada'].includes(tipo)) return 'citas';
  if (['nuevo_mensaje', 'nuevo_mensaje_colaboracion'].includes(tipo)) return 'mensajes';
  if (tipo.startsWith('admin_')) return 'admin';
  return 'sistema';
}

export function getNotificacionIcon(tipo: string): string {
  switch (tipo) {
    case 'solicitud_urgente': case 'alerta_sos': case 'admin_urgencia': return '🚨';
    case 'nueva_solicitud': return '📋';
    case 'solicitud_aceptada': return '✅';
    case 'solicitud_enviada': return '📤';
    case 'solicitud_liberada': return '🔄';
    case 'sin_profesionales': return '⚠️';
    case 'cita_confirmada': return '📅';
    case 'cita_cancelada': return '❌';
    case 'atencion_completada': return '🏥';
    case 'verificacion_aprobada': return '✨';
    case 'documento_rechazado': return '📄';
    case 'nuevo_mensaje': case 'nuevo_mensaje_colaboracion': return '💬';
    case 'admin_nuevo_usuario': return '👤';
    case 'admin_documento_pendiente': case 'admin_verificacion_pendiente': return '🔍';
    case 'admin_nueva_solicitud': return '📋';
    case 'admin_pago_recibido': return '💰';
    default: return '🔔';
  }
}

export function getNotificacionColor(tipo: string): string {
  const cat = getNotificacionCategoria(tipo);
  switch (cat) {
    case 'urgentes': return 'border-l-destructive bg-destructive/5';
    case 'citas': return 'border-l-primary bg-primary/5';
    case 'mensajes': return 'border-l-accent bg-accent/5';
    case 'admin': return 'border-l-secondary bg-secondary/5';
    default: return 'border-l-muted-foreground bg-muted/30';
  }
}

/* ─── Toast for realtime ─── */
function showRealtimeToast(n: Notificacion) {
  const icon = getNotificacionIcon(n.tipo);
  const isUrgent = getNotificacionCategoria(n.tipo) === 'urgentes';

  toast(n.titulo, {
    description: n.mensaje.length > 120 ? n.mensaje.slice(0, 120) + '…' : n.mensaje,
    icon,
    duration: isUrgent ? 8000 : 5000,
    position: 'top-right',
    className: isUrgent ? 'border-destructive/50 shadow-lg' : '',
  });
}

/* ─── Hook ─── */
export function useNotificaciones() {
  const { user } = useAuth();
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState<NotificacionCategoria>('todas');
  const prevIdsRef = useRef<Set<string>>(new Set());

  const fetchNotificaciones = useCallback(async (isRealtime = false) => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('notificaciones')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      const items = (data || []) as Notificacion[];

      // Detect genuinely new notifications for sound/vibration/toast
      if (isRealtime && prevIdsRef.current.size > 0) {
        const newItems = items.filter(n => !n.leida && !prevIdsRef.current.has(n.id));
        if (newItems.length > 0) {
          // Use the most urgent notification's sound
          const mostUrgent = newItems.find(n => getNotificacionCategoria(n.tipo) === 'urgentes') || newItems[0];
          playNotificationSound(mostUrgent.tipo);
          vibrateDevice(mostUrgent.tipo);
          // Show toast for each new notification (max 3)
          newItems.slice(0, 3).forEach(n => showRealtimeToast(n));
        }
      }

      prevIdsRef.current = new Set(items.map(n => n.id));
      setNotificaciones(items);
      setHasUnread(items.some(n => !n.leida));
    } finally {
      setLoading(false);
    }
  }, [user]);

  const marcarLeida = useCallback(async (id: string) => {
    await supabase.from('notificaciones').update({ leida: true }).eq('id', id);
    setNotificaciones(prev => prev.map(n => (n.id === id ? { ...n, leida: true } : n)));
    setHasUnread(prev => {
      const remaining = notificaciones.filter(n => !n.leida && n.id !== id);
      return remaining.length > 0;
    });
  }, [notificaciones]);

  const marcarTodasLeidas = useCallback(async () => {
    if (!user) return;
    await supabase
      .from('notificaciones')
      .update({ leida: true })
      .eq('user_id', user.id)
      .eq('leida', false);
    setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
    setHasUnread(false);
  }, [user]);

  const eliminarNotificacion = useCallback(async (id: string) => {
    await supabase.from('notificaciones').delete().eq('id', id);
    setNotificaciones(prev => {
      const updated = prev.filter(n => n.id !== id);
      setHasUnread(updated.some(n => !n.leida));
      return updated;
    });
  }, []);

  const eliminarTodas = useCallback(async () => {
    if (!user) return;
    await supabase.from('notificaciones').delete().eq('user_id', user.id);
    setNotificaciones([]);
    setHasUnread(false);
  }, [user]);

  const filteredNotificaciones = filtro === 'todas'
    ? notificaciones
    : notificaciones.filter(n => getNotificacionCategoria(n.tipo) === filtro);

  const unreadCount = notificaciones.filter(n => !n.leida).length;

  useEffect(() => {
    if (!user) return;
    fetchNotificaciones(false);

    const channel = supabase
      .channel(`notif-badge-${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notificaciones',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        fetchNotificaciones(true);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, fetchNotificaciones]);

  return {
    notificaciones: filteredNotificaciones,
    allNotificaciones: notificaciones,
    hasUnread,
    unreadCount,
    loading,
    filtro,
    setFiltro,
    marcarLeida,
    marcarTodasLeidas,
    eliminarNotificacion,
    eliminarTodas,
    refetch: fetchNotificaciones,
  };
}
